import Http from 'services/http';
import { Alias } from 'types/drupal/Alias';
import { Article, ArticleResponseData, GetArticleOptions } from 'types/drupal/ArticleNode';
import { BrandInfoApiResponse, BrandInfoInstrumentsApiResponse, BrandInfoModelsApiResponse, BrandsInfoApiResponse } from 'types/drupal/BrandInfo';
import FeaturedResponseData from 'types/drupal/FeaturedResponseData';
import { HeroSlide } from 'types/drupal/HeroSlideNode';
import { InstrumentGroup } from 'types/drupal/InstrumentGroupNode';
import { CategoryMapping, DetailInstrument, GetInstrumentsOptions, InventoryResponseData } from 'types/drupal/InstrumentNode';
import { Page } from 'types/drupal/PageNode';
import { StaffPageUser, User } from 'types/drupal/User';
import { GetVintageGuitarArticleOptions, VintageGuitarArticle, VintageGuitarArticleResponseData } from 'types/drupal/VintageGuitarArticleNode';

const api = new Http({ host: process.env.API_URL });

// TODO: evaluate benefits (if any) of making Http class methods abstract or (implicitly) virtual then overriding them via subclass rather than via the instance assignment below
api.getHeaders = (): HeadersInit => {
    // TODO: probably need an access token
    return {
        Accept: 'application/json',
        'Content-Type': 'application/json'
    };
};

api.parseJsonResponse = (json: any): any => {
    if (typeof json === 'undefined') {
        throw new Error('No JSON returned on API response.');
    }
    return json.data;
};

export default api;

export class AliasApi {
    public static async getInstrumentAliasBySku(sku: string): Promise<Alias> {
        return api.get(`api/alias/instruments/${sku}`);
    }
}

// tslint:disable-next-line:max-classes-per-file
export class InstrumentsApi {
    public static async getInstruments(getOptions?: Partial<GetInstrumentsOptions>): Promise<InventoryResponseData> {
        if (!getOptions) return api.get('api/instruments');
        return api.get('api/instruments?', getOptions);
    }
    public static async getInstrument(nodeAlias: string): Promise<DetailInstrument> {
        return api.get('/api/nodes?', { path: nodeAlias });
    }
    public static async getCategories(): Promise<CategoryMapping[]> {
        return api.get('/api/categories');
    }
    public static async getYears(): Promise<string[]> {
        return api.get('/api/years');
    }
    public static async getModels(getOptions?: Partial<GetInstrumentsOptions>): Promise<string[]> {
        if (!getOptions) return api.get('/api/models');
        return api.get('api/models?', getOptions);
    }
    public static async getManufacturers(getOptions?: Partial<GetInstrumentsOptions>): Promise<string[]> {
        if (!getOptions) return api.get('/api/manufacturers');
        return api.get('api/manufacturers?', getOptions);
    }
}

// tslint:disable-next-line:max-classes-per-file
export class NodeApi {
    public static async getNode(path: string): Promise<Page | Article | DetailInstrument | InstrumentGroup> {
        return api.get('/api/nodes?', { path });
    }
    // tslint:disable-next-line:prefer-array-literal    literal causes ambiguity/syntax issues
    public static async getFeatured(getOptions?: any): Promise<FeaturedResponseData> {
        // TODO: define getOptions type if neccesary (options only used for debug currently)
        return api.get('/api/featured?', getOptions);
    }

    public static async getInstrumentGroups(nid: number): Promise<InstrumentGroup[]> {
        return api.get('/api/instrumentgroups/' + nid);
    }
}

// tslint:disable-next-line:max-classes-per-file
export class PagesApi {
    public static async getPage(path: string): Promise<Page> {
        return api.get('/api/nodes?', { path });
    }
}

// tslint:disable-next-line:max-classes-per-file
export class UsersApi {
    public static async getStaff(): Promise<StaffPageUser[]> {
        return api.get('/api/staff');
    }

    public static async getUser(path: string): Promise<User> {
        return api.get('/api/users?', { path });
    }
}

// tslint:disable-next-line:max-classes-per-file
export class ArticlesApi {
    public static async getArticles(getOptions?: Partial<GetArticleOptions>): Promise<ArticleResponseData> {
        if (getOptions) return api.get('/api/articles?', getOptions);
        return api.get('/api/articles');
    }

    public static async getArticle(path: string): Promise<Article> {
        return api.get('/api/nodes?', { path });
    }

    public static async getNewsletters(getOptions?: Partial<GetArticleOptions>): Promise<ArticleResponseData> {
        if (getOptions) return api.get('/api/newsletters?', getOptions);
        return api.get('/api/newsletters');
    }

    public static async getNewsletter(path: string): Promise<Article> {
        return api.get('/api/nodes?', { path });
    }
}

// tslint:disable-next-line:max-classes-per-file
export class HeroSlidesApi {
    public static async getSlides(): Promise<HeroSlide[]> {
        return api.get('/api/slides');
    }
}

// tslint:disable-next-line:max-classes-per-file
export class BrandInfoApi {

    public static async getBrands(): Promise<BrandsInfoApiResponse> {
        return api.get('/api/brands/');
    }

    public static async getBrand(brandName: string): Promise<BrandInfoApiResponse> {
        return api.get('/api/brands/' +  brandName);
    }

    public static async getBrandModels(brandName: string, year: string): Promise<BrandInfoModelsApiResponse> {
        const yearCoalesce = year || '';
        return api.get('/api/brands/' +  brandName + '/models/' + yearCoalesce);
    }

    public static async getBrandInstruments(brandName: string, year: string,  page: string, perPage: number): Promise<BrandInfoInstrumentsApiResponse> {
        return api.get('/api/brands/' +  brandName + '/instruments/' + year + '?', { page, perPage });
    }

    public static async getBrandInstrumentsWithModel(brandName: string, brandModel: string, year: string, page: string, perPage: number): Promise<BrandInfoInstrumentsApiResponse> {
        const brandModelCoalesce = brandModel || '';
        const yearCoalesce =  year ? year + '/' : '';
        return api.get('/api/brands/' +  brandName + '/instruments/' + yearCoalesce  + brandModelCoalesce + '?', { page, perPage });
    }
}

// tslint:disable-next-line:max-classes-per-file
export class VintageGuitarArticlesApi {
    public static async getVintageGuitarArticles(getOptions?: Partial<GetVintageGuitarArticleOptions>): Promise<VintageGuitarArticleResponseData> {
        if (getOptions) return api.get('/api/vintageguitars?', getOptions);
        return api.get('/api/vintageguitars');
    }

    public static async getVintageGuitarArticle(path: string): Promise<VintageGuitarArticle> {
        return api.get('/api/nodes?', { path });
    }
}



// WEBPACK FOOTER //
// ./src/providers/api.ts